/**
 * BEFORE THE WORK HAS BEEN STARTED — what is missing, the records, and the one
 * genuinely human decision.
 *
 * WHY THE SCREEN KNOWS IT IS HERE. The work is in `DRAFT`, which the contract
 * glosses as "opened; nothing has been started". That is the server's own
 * derived state, not a condition this screen worked out.
 *
 * THE BLOCKERS ARE THE SERVER'S SENTENCES. They are written in the
 * accountant's terms on purpose — "Enclose the trial balance that carries the
 * control account", never "control.evidenceReferenceIds must have at least one
 * element" — so they are shown exactly as they arrive, in the order they
 * arrive.
 *
 * ═══ R5.1 — THE CONTROL ACCOUNT ═══
 *
 * The first independent test was offered **Equipment** for an accounts payable
 * reconciliation, and this file is half the reason. The server proposed
 * nothing — it could not read "AP" — and the screen then fell back to
 * `candidates[0]`, which on a chart sorted by code is whatever asset comes
 * first. THE SERVER REFUSED TO GUESS AND THE BROWSER GUESSED ANYWAY. That is
 * the worst shape a default can take: it looks like a recommendation.
 *
 * Three things changed here.
 *   · NOTHING IS PRE-SELECTED UNLESS THE SERVER PROPOSED IT. When it proposed
 *     nothing, nothing is chosen and the work cannot be started until a person
 *     picks a line. An unanswered question stays visibly unanswered.
 *   · THE LIST IS SEARCHABLE. A real trial balance has sixty-seven lines, and
 *     scanning sixty-seven radio buttons is not a professional act. The filter
 *     narrows what is SHOWN; it selects nothing and it removes nothing from
 *     the record.
 *   · THE LINE CAN BE INSPECTED. Every candidate carries its own row from the
 *     client's export — the code, the name, the classification, the figures —
 *     so the choice is confirmed by looking at the line rather than trusting
 *     its label. Where Leveri did propose, it says WHY.
 */
import { useMemo, useState } from "react";
import {
  WorkActionResponse,
  type ControlCandidate, type StartReadiness, type WorkFile,
} from "@leveri/contracts";
import { Button, Citation, Definitions, Notice, Reading, RuledList, Section, SectionHead, TextField } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";
import { EncloseRecord } from "./enclose.js";
import { useKeyring } from "./keys.js";

export interface StartSectionProps {
  workId: string;
  start: StartReadiness;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}

/** The server-owned identity of the admitted trial-balance line. */
const identify = (option: ControlCandidate) => option.id;

export function StartSection({ workId, start, onWork, onOutcome }: StartSectionProps) {
  /**
   * WHAT IS SELECTED IS THE PERSON'S CHOICE, OR THE SERVER'S PROPOSAL — never
   * a position in a list. The candidates are re-read from the server every
   * time a record is enclosed, and an index held across that would quietly
   * point at a different line than the one that was looked at.
   */
  const [picked, setPicked] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [opened, setOpened] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const keyFor = useKeyring();

  const proposed = start.candidates.find((option) => option.proposed) ?? null;
  // NO FALLBACK TO THE FIRST ROW. Where the server proposed nothing, nothing
  // is chosen, and the work cannot start until a person chooses.
  const candidate: ControlCandidate | null =
    start.candidates.find((option) => identify(option) === picked) ?? proposed;

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle === "") return start.candidates;
    return start.candidates.filter(
      (option) => option.label.toLowerCase().includes(needle) || option.rowText.some((cell) => cell.toLowerCase().includes(needle))
    );
  }, [start.candidates, filter]);

  async function begin() {
    if (!candidate || busy || !start.canStart) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await call("POST", `/api/work/${workId}/start`, WorkActionResponse, {
        control: { candidateId: candidate.id },
        // The act is "start this work on THIS control line". Choosing a
        // different line is a different act and takes a different key; a
        // double-click on the same one is the same act and takes the same.
        submissionKey: keyFor(`start:${candidate.id}`),
      });
      onOutcome(result.outcome);
      onWork(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
      setBusy(false);
    }
  }

  return (
    <>
      {/* Rendered only when there IS something outstanding. An empty section
          headed "what is still needed" would be the screen answering a
          question the server did not ask.

          R5.1: it is called RECORDS, not requirements. "Open requirements: 0"
          sat in the position ledger on this same screen while this section
          said things were still needed, and two accountants read the two as a
          contradiction. A request is something the capability raises once it
          has run; a record is something to go and fetch. */}
      {(start.blockers.length > 0 || start.missing.length > 0) && (
        <Section>
          <SectionHead>Records this work needs</SectionHead>
          <div className="flex flex-col gap-2.5">
            {start.blockers.map((blocker) => <Notice key={blocker} tone="attention">{blocker}</Notice>)}
          </div>
          {start.missing.length > 0 && (
            <div className="mt-4">
              {/* The kinds still needed, named by the server — the word and
                  the line saying what the record is for, both as they arrived. */}
              <Definitions
                compact
                items={start.missing.map((kind) => ({ key: kind.role, term: kind.label, value: kind.purpose }))}
              />
            </div>
          )}
        </Section>
      )}

      <Section>
        <SectionHead>Records</SectionHead>
        <EncloseRecord
          workId={workId}
          kinds={start.kinds}
          missing={start.missing.map((kind) => kind.role)}
          onEnclosed={onWork}
        />
      </Section>

      <Section>
        <SectionHead aside={start.candidates.length > 0 ? String(start.candidates.length) : null}>The control account</SectionHead>
        {start.candidates.length === 0 ? (
          <p className="text-sm text-stone">
            Leveri reads the control lines out of the trial balance itself. There are none to choose from until one is
            enclosed.
          </p>
        ) : (
          <>
            <p className="mb-3 text-[13px] text-stone">
              {proposed
                ? "These lines come from the record you enclosed. Leveri has proposed one; confirm it or pick another."
                : "These lines come from the record you enclosed. Leveri could not tell which one you meant, so it has proposed none — choose the control account."}
            </p>
            {start.candidates.length > 8 && (
              <div className="mb-3 max-w-[320px]">
                <TextField
                  label="Find a line"
                  placeholder="Part of the name, or a code"
                  value={filter}
                  disabled={busy}
                  onChange={(event) => setFilter(event.target.value)}
                />
              </div>
            )}
            <RuledList>
              {shown.map((option) => {
                const key = identify(option);
                const isOpen = opened === key;
                return (
                  <div key={key} className="py-1">
                    <label className="grid cursor-pointer grid-cols-[18px_minmax(0,1fr)_160px] items-baseline gap-4 py-2 hover:bg-band">
                      <input
                        type="radio"
                        name="control-account"
                        className="justify-self-center accent-forest"
                        checked={candidate ? key === identify(candidate) : false}
                        disabled={busy}
                        onChange={() => setPicked(key)}
                      />
                      <span className="min-w-0">
                        <span className="text-[15px] text-ink">{option.label}</span>
                        <Citation className="ml-2">{option.sheetName} › row {option.rowIndex}</Citation>
                        {option.proposed && option.proposedBecause && (
                          <span className="mt-1 block text-[13px] text-stone">{option.proposedBecause}</span>
                        )}
                      </span>
                      <span className="text-right text-[15px] text-ink [font-variant-numeric:tabular-nums]">
                        {option.amountText}
                      </span>
                    </label>
                    {/* THE LINE ITSELF, from the client's own export. A control
                        account is confirmed by looking at the row, not by
                        trusting a label that happens to contain a word. */}
                    <button
                      type="button"
                      onClick={() => setOpened(isOpen ? null : key)}
                      className="ml-[34px] text-[13px] text-stone transition-colors hover:text-ink"
                    >
                      {isOpen ? "Hide the row" : "Show the row"}
                    </button>
                    {isOpen && (
                      <div className="ml-[34px] mt-1.5 pb-2">
                        <p className="text-[13px] text-ink-soft [font-variant-numeric:tabular-nums]">
                          {option.rowText.join("  ·  ")}
                        </p>
                        <Citation as="div" className="mt-1">
                          {option.sheetName} › row {option.rowIndex} · {option.balanceSideLabel}
                        </Citation>
                      </div>
                    )}
                  </div>
                );
              })}
            </RuledList>
            {shown.length === 0 && (
              <p className="mt-3 text-sm text-stone">No line of the trial balance matches that.</p>
            )}
          </>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3.5">
          <Button disabled={busy || !start.canStart || !candidate} onClick={() => void begin()}>Start the work</Button>
          <span className="text-[13px] text-stone">
            {start.candidates.length > 0 && !candidate
              ? "Choose the control account first. Nothing is recorded until the work is started."
              : "Nothing is recorded until the work is started."}
          </span>
        </div>
        {busy && <div className="mt-3"><Reading>Starting the work…</Reading></div>}
        {failure && <div className="mt-3"><Notice>{failure}</Notice></div>}
      </Section>
    </>
  );
}
